import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { BuilderBlocks } from "@builder.io/react";
import type { BuilderElement } from "@builder.io/sdk";

type HeroBackgroundProps = {
  posterSrc: string;
  posterAlt?: string;
  posterPriority?: boolean;
  posterQuality?: number;
  videoMp4Src?: string;
  videoWebmSrc?: string;
  videoPreload?: "none" | "metadata" | "auto";
  enableVideoFromMinWidth?: number;
  minHeightDesktop?: string;
  minHeightMobile?: string;
  overlayClassName?: string;
  children?: React.ReactNode;
  builderBlock?: BuilderElement;
  // builderContext?: unknown;
};

type InViewOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  once?: boolean;
};

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQueryList = window.matchMedia(query);
    const update = () => setMatches(mediaQueryList.matches);
    update();

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", update);
    } else {
      mediaQueryList.addListener(update);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener("change", update);
      } else {
        mediaQueryList.removeListener(update);
      }
    };
  }, [query]);

  return matches;
}

function usePrefersReducedMotion() {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

function useInView<T extends Element>(
  ref: React.RefObject<T>,
  { root = null, rootMargin = "0px", threshold = 0, once = false }: InViewOptions = {}
) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;

    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { root, rootMargin, threshold }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [ref, root, rootMargin, threshold, once]);

  return inView;
}

export default function HeroBackground({
  posterSrc,
  posterAlt = "",
  posterPriority = true,
  posterQuality = 75,
  videoMp4Src,
  videoWebmSrc,
  videoPreload = "none",
  enableVideoFromMinWidth = 1024,
  minHeightDesktop = "60vh",
  minHeightMobile = "50vh",
  overlayClassName,
  children,
  builderBlock,
}: HeroBackgroundProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hasMounted, setHasMounted] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  const isDesktop = useMediaQuery(`(min-width: ${enableVideoFromMinWidth}px)`);
  const prefersReducedMotion = usePrefersReducedMotion();
  const inView = useInView(wrapperRef, { threshold: 0.25, once: true });

  const hasVideoSources = Boolean(videoMp4Src || videoWebmSrc);
  const allowVideo = hasMounted && hasVideoSources && isDesktop && !prefersReducedMotion;
  const minHeight = isDesktop ? minHeightDesktop : minHeightMobile;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!allowVideo) {
      setIsIdle(false);
      setShouldLoadVideo(false);
    }
  }, [allowVideo]);

  useEffect(() => {
    if (!allowVideo || !inView || isIdle) return;

    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const markIdle = () => setIsIdle(true);

    if ("requestIdleCallback" in window) {
      idleId = (window as Window & { requestIdleCallback: (cb: () => void) => number })
        .requestIdleCallback(markIdle);
    } else {
      timeoutId = (window as any).setTimeout(markIdle, 1500);
    }

    return () => {
      if (idleId !== null && "cancelIdleCallback" in window) {
        (window as Window & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [allowVideo, inView, isIdle]);

  useEffect(() => {
    if (!shouldLoadVideo && allowVideo && inView && isIdle) {
      setShouldLoadVideo(true);
    }
  }, [allowVideo, inView, isIdle, shouldLoadVideo]);

  const hasBuilderChildren = Boolean(builderBlock?.children?.length);

  return (
    <div
      ref={wrapperRef}
      className="relative w-full overflow-hidden"
      style={{ minHeight }}
    >
      <div className="absolute inset-0">
        {/* Poster optimizado para LCP; el video se monta solo cuando está in-view y en idle. */}
        <Image
          src={posterSrc}
          alt={posterAlt}
          fill
          sizes="100vw"
          priority={posterPriority}
          quality={posterQuality}
          className="h-full w-full object-cover"
        />
        {shouldLoadVideo && (
          <video
            className="absolute inset-0 h-full w-full object-cover"
            muted
            loop
            playsInline
            autoPlay
            preload={videoPreload}
          >
            {videoWebmSrc && <source src={videoWebmSrc} type="video/webm" />}
            {videoMp4Src && <source src={videoMp4Src} type="video/mp4" />}
          </video>
        )}
      </div>

      {overlayClassName ? (
        <div className={`absolute inset-0 ${overlayClassName}`} />
      ) : null}

      <div className="relative z-5">
        {hasBuilderChildren ? (
          <BuilderBlocks
            parentElementId={builderBlock?.id}
            dataPath="children"
            blocks={builderBlock?.children}
            // context={builderContext}
          />
        ) : (
          children
        )}
      </div>
    </div>
  );
}
